import React, { useMemo, useState } from 'react';
import PageHeader from '../../components/shared/PageHeader';
import DataToolbar, { StatusPill, Card } from '../../components/shared/DataToolbar';
import { LoadingState, ErrorState, EmptyResults } from '../../components/shared/Pending';
import { useApi } from '../../api/useApi';
import { getCustomer, listCustomers } from '../../api/hostApi';
import { CalendarDays, Loader2, Mail, Phone, Users } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const money = (value, currency = 'USD') => {
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(value || 0)); }
  catch (_) { return `$${Number(value || 0).toFixed(2)}`; }
};

export default function HostCustomers() {
  const { data, loading, error, refetch } = useApi(listCustomers, []);
  const rows = useMemo(() => Array.isArray(data) ? data : (data?.items || data || []), [data]);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const filtered = rows.filter((c) => !q || `${c.name || ''} ${c.email || ''} ${c.phone || ''}`.toLowerCase().includes(q.toLowerCase()));

  const openCustomer = async (customer) => {
    setSelected({ customer, bookings: [] });
    setDetailLoading(true);
    try {
      const detail = await getCustomer(customer.id);
      setSelected(detail || { customer, bookings: [] });
    } catch (_) {
      setSelected({ customer, bookings: [] });
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div>
      <PageHeader title="Customers" description="Guests who have booked one or more of your properties." />
      <DataToolbar search={q} onSearch={setQ} placeholder="Search by name, email or phone…" />
      {loading ? <LoadingState /> : error ? <ErrorState error={error} onRetry={refetch} />
        : filtered.length === 0 ? <EmptyResults icon={Users} title="No customers yet" description="Once guests book your listings they will appear here." />
        : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map(c => (
              <button key={c.id} type="button" onClick={() => openCustomer(c)} className="text-left">
                <Card className="p-5 card-hover h-full">
                  <div className="flex items-center gap-3">
                    {c.avatar ? <img src={c.avatar} className="h-12 w-12 rounded-full object-cover" alt="" /> : <div className="h-12 w-12 rounded-full bg-slate-100 grid place-items-center font-semibold text-slate-500">{(c.name || c.email || '?').slice(0, 1).toUpperCase()}</div>}
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 truncate">{c.name}</div>
                      <div className="text-xs text-slate-500 truncate">{c.email}</div>
                    </div>
                    <StatusPill status={c.status || 'active'} />
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-500">Bookings</div><div className="text-lg font-bold text-slate-900 tabular-nums">{c.total_bookings ?? c.totalBookings ?? '—'}</div></div>
                    <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-500">Total spent</div><div className="text-lg font-bold text-slate-900 tabular-nums">{c.total_spent != null ? money(c.total_spent) : '—'}</div></div>
                  </div>
                  <div className="mt-3 text-xs font-medium text-teal-700">View guest history</div>
                </Card>
              </button>
            ))}
          </div>
        )
      }

      <CustomerDetail value={selected} loading={detailLoading} onOpenChange={(open) => { if (!open) setSelected(null); }} />
    </div>
  );
}

function CustomerDetail({ value, loading, onOpenChange }) {
  const customer = value?.customer;
  const bookings = value?.bookings || [];
  return (
    <Dialog open={Boolean(value)} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto">
        {customer && (
          <>
            <DialogHeader>
              <div className="flex items-center justify-between gap-4">
                <DialogTitle>{customer.name || 'Guest details'}</DialogTitle>
                {loading && <span className="inline-flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading history</span>}
              </div>
            </DialogHeader>
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg bg-slate-50 p-4"><div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500"><Mail className="h-3.5 w-3.5" /> Email</div><div className="mt-2 text-sm font-medium text-slate-900 break-all">{customer.email || '—'}</div></div>
              <div className="rounded-lg bg-slate-50 p-4"><div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500"><Phone className="h-3.5 w-3.5" /> Phone</div><div className="mt-2 text-sm font-medium text-slate-900">{customer.phone || '—'}</div></div>
            </div>
            <div className="mt-6 flex items-center justify-between"><h3 className="font-semibold text-slate-900">Booking history</h3><span className="text-xs text-slate-500">{bookings.length} booking{bookings.length === 1 ? '' : 's'}</span></div>
            <div className="mt-3 divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
              {!loading && bookings.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No booking history available.</div>}
              {bookings.map((booking) => (
                <div key={booking.id} className="p-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <div className="min-w-0 flex-1"><div className="font-medium text-slate-900 truncate">{booking.property?.title || `Booking #${booking.id}`}</div><div className="mt-1 inline-flex items-center gap-1.5 text-xs text-slate-500"><CalendarDays className="h-3.5 w-3.5" /> {booking.check_in} → {booking.check_out}</div></div>
                  <div className="sm:text-right"><StatusPill status={booking.status} /><div className="mt-1 text-sm font-semibold text-slate-900">{money(booking.total_amount, booking.currency)}</div></div>
                </div>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
